import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Container,
  Stack,
  Text,
  Title,
  Button,
  TextInput,
  Textarea,
  NumberInput,
  Alert,
  Paper,
  Group,
  Grid,
  Skeleton,
  Box,
  Anchor,
  ThemeIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconPackage,
  IconAlertCircle,
  IconCircleCheck,
} from "@tabler/icons-react";
import axios from "axios";
import {
  physicianPortalService,
  type SupplyRequestItem,
  type SupplyRequestSubmission,
} from "../../services/physicianPortal.service";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import {
  parseDateOnly,
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";

interface FormItem {
  itemKey: string;
  label: string;
  quantity: number;
  unit?: string | null;
}

export function PortalRequestSuppliesPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const labToday = useLabTodayDateOnly();

  // 1. Fetch Contact & Supply Config
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configError,
  } = useQuery({
    queryKey: ["portal", "contact-config"],
    queryFn: () => physicianPortalService.getContactConfig(),
    staleTime: 5 * 60 * 1000,
  });

  // 2. Fetch Identity for Email Prefill
  const { data: identity } = useQuery({
    queryKey: ["portal", "identity"],
    queryFn: () => physicianPortalService.getIdentity(),
    staleTime: 5 * 60 * 1000,
  });

  // 3. Setup useForm
  const form = useForm({
    initialValues: {
      surgeryCenterName: "",
      requesterName: "",
      requesterEmail: "",
      requesterPhone: "",
      items: [] as FormItem[],
      neededByDate: null as DateOnlyString | null,
      notes: "",
    },
    validate: {
      surgeryCenterName: (value: string) =>
        value.trim() ? null : "Surgery Center name is required",
      requesterName: (value: string) =>
        value.trim() ? null : "Requester name is required",
      requesterEmail: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : "Invalid email address",
      items: {
        quantity: (value: number) =>
          value < 0 ? "Quantity cannot be negative" : null,
      },
      notes: (value: string) =>
        !value || value.length <= 2000
          ? null
          : "Notes must be 2000 characters or less",
      neededByDate: (value: DateOnlyString | null) => {
        if (!value) return null;
        const dateOnly = parseDateOnly(value);
        if (!dateOnly) return "Invalid date format";
        return dateOnly < labToday
          ? "Needed-by date cannot be in the past"
          : null;
      },
    },
    validateInputOnBlur: true,
  });

  const setFieldValueRef = useRef(form.setFieldValue);
  setFieldValueRef.current = form.setFieldValue;

  // 4. Prefill Email when identity is loaded
  const identityEmail = identity?.email;
  const emailInitialized = useRef(false);
  useEffect(() => {
    if (identityEmail && !emailInitialized.current) {
      setFieldValueRef.current("requesterEmail", identityEmail);
      emailInitialized.current = true;
    }
  }, [identityEmail]);

  // 5. Populate form items when contact config is loaded
  const supplyItems = config?.supplyItems;
  const itemsInitialized = useRef(false);
  useEffect(() => {
    if (supplyItems && supplyItems.length > 0 && !itemsInitialized.current) {
      const itemsPayload = supplyItems.map((item) => ({
        itemKey: item.itemKey,
        label: item.label,
        quantity: 0,
        unit: item.unit,
      }));
      setFieldValueRef.current("items", itemsPayload);
      itemsInitialized.current = true;
    }
  }, [supplyItems]);

  // 6. Submit Mutation
  const submitMutation = useMutation({
    mutationFn: (payload: SupplyRequestSubmission) =>
      physicianPortalService.submitSupplyRequest(payload),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        title: "Request submitted",
        message:
          res.message || "Your supply request has been successfully received.",
      });
      setIsSubmitted(true);
    },
    onError: (err: any) => {
      let errMsg = "Unable to submit supply request. Please try again.";
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 400) {
          errMsg = "Please verify all required form fields.";
        } else if (err.response?.status === 503) {
          errMsg = "The supply request service is currently unavailable.";
        }
      }
      notifications.show({
        color: "red",
        title: "Submission failed",
        message: errMsg,
      });
    },
  });

  // 7. Form Handlers wrapped in useCallback
  const handleResetForm = useCallback(() => {
    form.reset();
    setIsSubmitted(false);
    // Re-populate the supply items list from config
    if (config?.supplyItems) {
      const itemsPayload = config.supplyItems.map((item) => ({
        itemKey: item.itemKey,
        label: item.label,
        quantity: 0,
        unit: item.unit,
      }));
      form.setFieldValue("items", itemsPayload);
    }
    // Re-populate identity email
    if (identity?.email) {
      form.setFieldValue("requesterEmail", identity.email);
    }
  }, [config?.supplyItems, identity?.email, form]);

  const handleSubmit = useCallback(
    (values: typeof form.values) => {
      // Validate that at least one item has quantity > 0
      const chosenItems = values.items.filter((item) => item.quantity > 0);
      if (chosenItems.length === 0) {
        form.setFieldError(
          "items",
          "Please select a quantity of 1 or more for at least one item."
        );
        notifications.show({
          color: "red",
          title: "Validation error",
          message:
            "You must request at least one item with a quantity greater than zero.",
        });
        return;
      }

      const payload: SupplyRequestSubmission = {
        surgeryCenterName: values.surgeryCenterName,
        requesterName: values.requesterName,
        requesterEmail: values.requesterEmail,
        requesterPhone: values.requesterPhone || null,
        items: chosenItems as SupplyRequestItem[],
        neededByDate: values.neededByDate
          ? requireDateOnlyString(values.neededByDate, "Needed-by date")
          : null,
        notes: values.notes || null,
      };

      submitMutation.mutate(payload);
    },
    [submitMutation, form]
  );

  // Derived Values
  const cleanedPhoneHref = useMemo(() => {
    if (!config?.supportPhone) return "";
    return `tel:${config.supportPhone.replace(/[^\d+]/g, "")}`;
  }, [config?.supportPhone]);

  const configEmailHref = useMemo(() => {
    if (!config?.supportEmail) return "";
    return `mailto:${config.supportEmail}`;
  }, [config?.supportEmail]);

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up {
          animation: fadeUp 0.35s ease forwards;
        }
      `}</style>

      <Container size="lg" py="lg" className="fade-up">
        <Stack gap="xl">
          {/* Page header */}
          <Paper
            py="xl"
            px={0}
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-2)",
              background: "transparent",
            }}
            radius={0}
          >
            <Stack gap={4}>
              <Title
                order={1}
                c="blue.9"
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontWeight: 400,
                  fontSize: 34,
                }}
              >
                Request Supplies
              </Title>
            </Stack>
          </Paper>

          {/* Configuration Loading State */}
          {isConfigLoading && (
            <Stack gap="md">
              <Skeleton height={40} width="30%" />
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Paper withBorder p="xl" radius="md">
                    <Stack gap="md">
                      <Skeleton height={50} />
                      <Skeleton height={50} />
                      <Skeleton height={50} />
                      <Skeleton height={80} />
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <Paper withBorder p="xl" radius="md">
                    <Stack gap="md">
                      <Skeleton height={30} />
                      <Skeleton height={150} />
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Stack>
          )}

          {/* Configuration Load Error */}
          {configError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Unable to load supply form"
              role="alert"
            >
              We couldn't load the form options. Please check your internet
              connection or contact the lab directly to order supplies.
            </Alert>
          )}

          {/* Form Content */}
          {!isConfigLoading && !configError && config && (
            <>
              {isSubmitted ? (
                /* Success Confirmation Panel */
                <Paper
                  withBorder
                  p="xl"
                  radius="md"
                  style={{ textAlign: "center" }}
                >
                  <Stack align="center" gap="lg" py="xl">
                    <ThemeIcon size={64} radius="xl" color="teal">
                      <IconCircleCheck size={40} />
                    </ThemeIcon>
                    <Stack gap={4}>
                      <Title
                        order={2}
                        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                      >
                        Supply Request Received
                      </Title>
                      <Text
                        c="dimmed"
                        size="sm"
                        style={{ maxWidth: 500, margin: "0 auto" }}
                      >
                        Thank you! Your supply request has been successfully
                        submitted and is being processed. We will coordinate
                        dispatch shortly.
                      </Text>
                    </Stack>
                    <Button
                      variant="outline"
                      color="teal"
                      onClick={handleResetForm}
                      style={{ maxWidth: 220, margin: "0 auto" }}
                    >
                      Request More Supplies
                    </Button>
                  </Stack>
                </Paper>
              ) : (
                /* Active Request Form */
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Grid gutter={32}>
                    {/* Left Column: Requester Information */}
                    <Grid.Col span={{ base: 12, md: 7 }}>
                      <Paper withBorder p="xl" radius="md" bg="white">
                        <Stack gap="md">
                          <Text fw={600} size="lg">
                            Requester Details
                          </Text>

                          <TextInput
                            label="Surgery Center Name"
                            placeholder="Enter the name of your surgery center"
                            required
                            error={form.errors.surgeryCenterName}
                            {...form.getInputProps("surgeryCenterName")}
                          />

                          <Grid gutter="md">
                            <Grid.Col span={{ base: 12, sm: 6 }}>
                              <TextInput
                                label="Requester Name"
                                placeholder="Your name"
                                required
                                error={form.errors.requesterName}
                                {...form.getInputProps("requesterName")}
                              />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 6 }}>
                              <TextInput
                                label="Requester Phone"
                                placeholder="Optional"
                                error={form.errors.requesterPhone}
                                {...form.getInputProps("requesterPhone")}
                              />
                            </Grid.Col>
                          </Grid>

                          <TextInput
                            label="Requester Email"
                            placeholder="name@surgerycenter.com"
                            required
                            error={form.errors.requesterEmail}
                            {...form.getInputProps("requesterEmail")}
                          />

                          <DateInput
                            label="Needed-by Date"
                            placeholder="Select date (optional)"
                            clearable
                            minDate={labToday}
                            popoverProps={{
                              position: "bottom-start",
                              offset: 8,
                              withinPortal: true,
                            }}
                            error={form.errors.neededByDate}
                            {...form.getInputProps("neededByDate")}
                          />

                          <Textarea
                            label="Additional Notes / Instructions"
                            placeholder="Please specify if you need particular sizes or have specific delivery instructions (optional)"
                            minRows={4}
                            error={form.errors.notes}
                            {...form.getInputProps("notes")}
                          />
                        </Stack>
                      </Paper>
                    </Grid.Col>

                    {/* Right Column: Supply Items Selection */}
                    <Grid.Col span={{ base: 12, md: 5 }}>
                      <Stack gap="lg">
                        <Paper withBorder p="xl" radius="md" bg="white">
                          <Stack gap="md">
                            <Group justify="space-between" align="center">
                              <Text fw={600} size="lg">
                                Select Supplies
                              </Text>
                              <Box>
                                <ThemeIcon
                                  variant="light"
                                  color="indigo"
                                  radius="md"
                                  size="lg"
                                >
                                  <IconPackage size={20} />
                                </ThemeIcon>
                              </Box>
                            </Group>

                            <Text size="xs" c="dimmed">
                              Adjust quantities for each required supply item
                              below. At least one item must have a quantity of 1
                              or more.
                            </Text>

                            {/* Items validation message */}
                            {form.errors.items && (
                              <Alert
                                color="red"
                                icon={<IconAlertCircle size={16} />}
                                style={{ padding: "8px 12px" }}
                              >
                                <Text size="xs">{form.errors.items}</Text>
                              </Alert>
                            )}

                            {/* List of supply items */}
                            {form.values.items.length === 0 ? (
                              <Alert color="warning" title="No items available">
                                No pre-configured supply items were found.
                                Please use the notes field or contact us
                                directly.
                              </Alert>
                            ) : (
                              <Stack gap="xs" style={{ marginTop: 8 }}>
                                {form.values.items.map((item, index) => (
                                  <Group
                                    key={item.itemKey}
                                    justify="space-between"
                                    align="center"
                                    py="xs"
                                    style={{
                                      borderBottom:
                                        index < form.values.items.length - 1
                                          ? "1px solid var(--mantine-color-gray-1)"
                                          : "none",
                                    }}
                                  >
                                    <Stack
                                      gap={0}
                                      style={{ flex: 1, marginRight: 16 }}
                                    >
                                      <Text fw={500} size="sm">
                                        {item.label}
                                      </Text>
                                      {item.unit && (
                                        <Text size="xs" c="dimmed">
                                          Unit: {item.unit}
                                        </Text>
                                      )}
                                    </Stack>
                                    <NumberInput
                                      min={0}
                                      max={999}
                                      allowNegative={false}
                                      allowDecimal={false}
                                      style={{ width: 100 }}
                                      {...form.getInputProps(
                                        `items.${index}.quantity`
                                      )}
                                    />
                                  </Group>
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </Paper>

                        {/* Submission Failure Actionable Assistance */}
                        {submitMutation.isError && (
                          <Alert
                            color="red"
                            icon={<IconAlertCircle size={16} />}
                            title="Submission Failed"
                            role="alert"
                          >
                            <Stack gap="xs">
                              <Text size="xs">
                                Your request could not be sent online due to a
                                network or server issue. Please try submitting
                                again, or place your supply order directly:
                              </Text>
                              <Group gap="sm">
                                <Anchor
                                  href={configEmailHref}
                                  size="xs"
                                  fw={600}
                                  color="red"
                                >
                                  Email: {config.supportEmail}
                                </Anchor>
                                {config.supportPhone && (
                                  <Anchor
                                    href={cleanedPhoneHref}
                                    size="xs"
                                    fw={600}
                                    color="red"
                                  >
                                    Phone: {config.supportPhone}
                                  </Anchor>
                                )}
                              </Group>
                            </Stack>
                          </Alert>
                        )}

                        <Button
                          type="submit"
                          size="md"
                          color="indigo"
                          fullWidth
                          loading={submitMutation.isPending}
                          disabled={config?.supplyItems?.length === 0}
                        >
                          Submit Supply Request
                        </Button>
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </form>
              )}
            </>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default PortalRequestSuppliesPage;
